/**
 * Menu items configuration for Vue app navigation
 *
 * 2026-03-16: Renamed Feeds to Sync in menu text
 * 2025-12-25: Fixed Integrations menu link - changed from /integrations to /dashboard/management (route that exists)
 */
import menuItemsHost from './menu-items-host'
import { getV2BaseUrl } from '@/utils/v2-base-url'

const menuItemsPro = [
  {
    key: 'dashboard',
    text: 'Dashboard',
    description: 'System overview and key metrics',
    icon: '$dashboard_v2',
    value: '/dashboard/system',
    value_v2: `/app/dashboard/system`, // Version 2 URL
    allowed_roles: ['admin'],
  },
  {
    key: 'bam_chat',
    text: 'BAM Chat',
    description: 'AI-powered chat assistant for property management',
    icon: 'mdi-robot-outline',
    allowed_roles: ['admin'],
    value_v2: '/app/bam-chat',
  },
  {
    key: 'multi_calendar',
    text: 'Multi calendar',
    description: 'View availability across all properties',
    allowed_roles: [
      'admin',
      'communication-manager',
      'communication-agent',
      'sales-manager',
      'sales-agent',
      'cleaning-supervisor',
      'cleaning-manager',
      'cleaner',
      'property-manager',
      'coordinator',
      'field-pm',
      'investor',
    ],
    icon: '$calendar_v2',
    value: '/dashboard/multi-calendar',
    value_v2: `/app/calendar`, // Version 2 URL
    tabs: ['Calendar', 'Blocks', 'Availability'],
  },
  {
    key: 'crm',
    text: 'CRM',
    description: 'Customer relationship management and guest experience',
    allowed_roles: [
      'admin',
      'communication-manager',
      'communication-agent',
      'sales-manager',
      'sales-agent',
      'property-manager',
      'coordinator',
    ],
    module: 'comm',
    abilities: ['crm-management'],
    abilityOrRole: true,
    icon: '$user_v2',
    value: '/dashboard/guest-experience',
    value_v2: `/app/crm`, // Version 2 URL
  },
  {
    key: 'owners',
    text: 'Owners',
    description: 'Property owner and investor management',
    icon: '$building_office_v2',
    submenu: [
      {
        key: 'owners_listings',
        text: 'Listings',
        description: 'Manage property listings and details',
        value: '/dashboard/listings',
        value_v2: `/app/listings`, // Version 2 URL
        allowed_roles: [
          'admin',
          'investor',
          'property-manager',
          'coordinator',
          'accountant',
          'field-pm',
        ],
        icon: '$home_v2',
        demo_only: true,
      },
      {
        key: 'owners_portfolio',
        text: 'Portfolio',
        description: 'Investment portfolio analysis and performance',
        icon: '$briefcase_v2',
        value: '/dashboard/portfolio',
        value_v2: `/app/dashboard/portfolio/overview`,
        module: 'ops',
        allowed_roles: ['admin', 'investor', 'property-manager'],
        tabs: ['Portfolio', 'Reviews'],
      },
    ],
  },
  {
    key: 'operations',
    text: 'Operations',
    description: 'Daily operations and task management',
    icon: '$cog_v2',
    submenu: [
      {
        key: 'operations_field_ops',
        text: 'Field Ops',
        description: 'Field operations and service call management',
        allowed_roles: [
          'admin',
          'communication-manager',
          'communication-agent',
          'sales-manager',
          'sales-agent',
          'cleaning-supervisor',
          'cleaning-manager',
          'property-manager',
          'coordinator',
          'hotel-reception',
          'accountant',
          'contractor',
          'cleaner',
          'field-pm',
          'pools-manager',
          'repairs-manager',
          'landscaping-manager',
        ],
        module: 'ops',
        icon: '$wrench_v2',
        value: '/dashboard/service-calls',
        value_v2: `/app/dashboard/service-calls`,
        demo_only: true,
        tabs: ['Area Report', 'My Tasks', 'Calendar', 'PM Summary'],
      },
      {
        key: 'operations_office_tasks',
        text: 'Office Tasks',
        description: 'Administrative tasks and workflow management',
        icon: '$document_text_v2',
        value: '/dashboard/taskim',
        value_v2: `/app/taskim/query`,
        allowed_roles: [
          'admin',
          'communication-manager',
          'communication-agent',
          'sales-agent',
          'sales-manager',
          'property-manager',
          'coordinator',
          'accountant',
        ],
        module: 'ops',
      },
      {
        key: 'operations_iot',
        text: 'IoT',
        description: 'Internet of Things device management',
        abilities: ['iot-management'],
        module: 'iot',
        icon: '$wifi_v2',
        value: '/dashboard/iot',
        value_v2: `/app/iot-devices`,
      },
      {
        key: 'operations_damage_protection',
        text: 'Damage Protection',
        allowed_roles: ['admin', 'property-manager', 'coordinator'],
        module: 'ops',
        icon: '$cpu_chip_v2',
        value: '/dashboard/damage-protection',
        value_v2: `/app/dashboard/damage-protection`,
      },
      {
        key: 'operations_storage',
        text: 'Storage',
        description: 'Inventory and storage management',
        allowed_roles: [
          'admin',
          'cleaning-manager',
          'cleaning-supervisor',
          'property-manager',
          'coordinator',
          'accountant',
          'field-pm',
        ],
        isPrivate: true,
        icon: '$cube_v2',
        module: 'projects',
        value: '/dashboard/inventory',
        value_v2: `/app/dashboard/inventory`,
      },
      {
        key: 'operations_projects',
        text: 'Projects',
        description: 'Property setup and development projects',
        allowed_roles: ['admin', 'property-manager', 'coordinator', 'field-pm'],
        isPrivate: true,
        icon: '$document_text_v2',
        abilities: ['projects'],
        module: 'projects',
        value: '/dashboard/listings-setup',
        value_v2: `/app/dashboard/listings-setup`,
      },
      {
        key: 'operations_processes',
        text: 'Processes',
        description: 'Workflow and process templates',
        allowed_roles: ['admin'],
        icon: '$share_v2',
        value: '/dashboard/processes',
        value_v2: `/app/dashboard/processes`,
      },
      {
        key: 'operations_automated_tasks',
        text: 'Automated Tasks',
        description: 'Configure automated rules and triggers',
        allowed_roles: ['admin', 'property-manager'],
        abilities: ['auto-rules-manager'],
        icon: '$cpu_chip_v2',
        value: '/dashboard/auto-tasks',
        value_v2: `/app/auto-task-rules`,
      },
      {
        key: 'operations_ops_settings',
        text: 'Ops Settings',
        description: 'Operations configuration and settings',
        icon: '$cog_v2',
        value: '/dashboard/operations-settings',
        value_v2: `/app/dashboard/operations-settings`,
        allowed_roles: ['admin', 'property-manager', 'coordinator'],
        module: 'ops',
        tabs: ['Task Catalog', 'Info Definitions'],
      },
    ],
  },
  {
    key: 'guests',
    text: 'Guests',
    description: 'Guest management and communication',
    icon: '$user_group_v2',
    submenu: [
      {
        key: 'guests_experience',
        text: 'Experience',
        description: 'Guest experience management and CRM',
        allowed_roles: [
          'admin',
          'communication-manager',
          'communication-agent',
          'sales-manager',
          'sales-agent',
          'property-manager',
          'coordinator',
          'field-pm',
        ],
        module: 'guest-exp',
        icon: '$face_smile_v2',
        value: '/dashboard/guest-mgmt',
        value_v2: `/app/dashboard/guest-mgmt`,
        tabs: [
          'Check-in/out',
          'Purchases',
          'Requests',
          'Checkin & store settings',
        ],
      },
      {
        key: 'guests_guest_book',
        text: 'Guest Book',
        description: 'Digital guest registration and information',
        icon: '$book_open_v2',
        value: '/dashboard/guest-book',
        value_v2: `/app/dashboard/guest-book`,
        allowed_roles: ['admin'],
        module: 'ops',
      },
      {
        key: 'guests_automated_messages',
        text: 'Automated Messages',
        description: 'Configure automated guest communications',
        allowed_roles: [
          'admin',
          'communication-manager',
          'communication-agent',
          'sales-manager',
          'sales-agent',
          'property-manager',
          'coordinator',
        ],
        icon: '$cpu_chip_v2',
        value: '/dashboard/auto-messages',
        value_v2: `/app/guests/automated-messages`,
        tabs: ['Guests', 'Users', 'WA templates'],
      },
    ],
  },
  {
    key: 'marketing',
    text: 'Marketing',
    description: 'Marketing tools and channel management',
    icon: '$megaphone_v2',
    submenu: [
      {
        key: 'marketing_channels',
        text: 'Channels',
        description: 'Channel manager and distribution platforms',
        allowed_roles: ['admin'],
        icon: '$globe_alt_v2',
        value: '/dashboard/cm',
        value_v2: `/app/channels`,
        module: 'channel-manager',
        tabs: ['Listings', 'Channels', 'Charge Profiles', 'Rate Plans'],
      },
      {
        key: 'marketing_reviews',
        text: 'Reviews',
        description: 'Guest reviews and reputation management',
        allowed_roles: [
          'admin',
          'communication-manager',
          'communication-agent',
          'sales-manager',
          'sales-agent',
          'property-manager',
          'coordinator',
        ],
        module: 'comm',
        icon: '$star_v2',
        value: '/dashboard/reviews-dashboard',
        value_v2: `/app/reviews`,
        tabs: [
          'Received Reviews',
          'Pending Reviews',
          'Channel links',
          'Listing logs',
        ],
      },
      {
        key: 'marketing_pricing',
        text: 'Pricing',
        description: 'Dynamic pricing and revenue optimization',
        icon: '$currency_dollar_v2',
        value: '/dashboard/pricing-panel',
        value_v2: `/app/pricings`,
        allowed_roles: ['admin'],
        abilities: ['pricing-manager'],
        module: 'pricing',
        tabs: [
          'Pricing',
          'Pricing Strategies',
          'Settings',
          'LOS',
          'Rate Imports',
        ],
      },
      {
        key: 'marketing_agents',
        text: 'Agents',
        description: 'Sales agent performance and reservations',
        icon: '$user_circle_v2',
        value: '/dashboard/agent-reservations',
        value_v2: `/app/dashboard/agent-reservations`,
        allowed_roles: ['admin', 'agent', 'sales-agent', 'sales-manager'],
        module: 'ops',
      },
      {
        key: 'marketing_websites',
        text: 'Websites',
        description: 'Website builder and direct booking sites',
        icon: '$globe_alt_v2',
        value: '/dashboard/websites',
        value_v2: `/app/dashboard/websites`,
        allowed_roles: ['admin'],
      },
    ],
  },
  {
    key: 'accounting',
    text: 'Accounting',
    description: 'Financial management and accounting',
    icon: '$building_library_v2',
    submenu: [
      {
        key: 'accounting_payables',
        text: 'Payables',
        description: 'Accounts payable and expense management',
        icon: '$credit_card_v2',
        value: '/dashboard/payables',
        value_v2: `/app/dashboard/payables`,
        module: 'accounting',
        allowed_roles: ['admin', 'accountant', 'property-manager'],
        tabs: [
          'Contractors Payments',
          'Owners Payments',
          'Management Payments',
          'Taxes Payments',
          'Transfers',
          'Reservation Balance',
        ],
      },
      {
        key: 'accounting_bank_feeds',
        text: 'Reconciliation',
        description: 'Bank transaction feeds and reconciliation',
        icon: '$building_library_v2',
        value: '/dashboard/bank-feeds',
        value_v2: `/app/finance/bank-transactions`,
        module: 'accounting',
        allowed_roles: ['admin', 'accountant', 'property-manager'],
        tabs: ['Bank Transactions', 'Bank Accounts', 'Deposits'],
      },
      {
        key: 'accounting_ledger',
        text: 'Ledger',
        description: 'General ledger and chart of accounts',
        icon: '$book_open_v2',
        value: '/dashboard/ledger-wallet',
        value_v2: `/app/dashboard/ledger-wallet`,
        module: 'accounting',
        allowed_roles: ['admin', 'accountant'],
        tabs: ['Trust Account', 'Account lines'],
      },
      {
        key: 'accounting_financials',
        text: 'Financials',
        description: 'Financial reports and analysis',
        icon: '$banknotes_v2',
        value: '/dashboard/financials',
        value_v2: `/app/dashboard/financials`,
        module: 'accounting',
        allowed_roles: [
          'admin',
          'accountant',
          'property-manager',
          'coordinator',
        ],
        tabs: [
          'Expenses',
          'Reservations',
          'Payments',
          'Expense settings',
          'Business model',
        ],
      },
      {
        key: 'accounting_invoices',
        text: 'Invoices',
        description: 'Invoice generation and management',
        icon: '$document_v2',
        value: '/dashboard/invoices',
        value_v2: `/app/dashboard/invoices`,
        allowed_roles: ['admin', 'hotel-reception'],
      },
      {
        key: 'accounting_charge_profiles',
        text: 'Charge Profiles',
        description: 'Manage tax and fee profiles for listings',
        allowed_roles: ['super-admin'],
        abilities: ['super-admin'],
        icon: '$document_v2',
        value: '/dashboard/tax-profiles',
        value_v2: `/app/dashboard/tax-profiles`,
      },
    ],
  },
  {
    key: 'configuration',
    text: 'Configuration',
    description: 'System configuration and settings',
    icon: '$cog_v2',
    submenu: [
      {
        key: 'configuration_settings',
        text: 'Settings',
        description: 'General system settings and user management',
        allowed_roles: [
          'admin',
          'property-manager',
          'coordinator',
          'communication-manager',
          'communication-agent',
          'sales-agent',
          'sales-manager',
        ],
        demo_only: true,
        abilities: [
          'estimates-config',
          'users-management',
          'real-estate-finance',
          'pricing-manager',
          'channel-management',
        ],
        icon: '$cog_v2',
        value: '/dashboard/management',
        value_v2: `/app/dashboard/management`,
        tabs: [
          'Users',
          'Settings',
          'Real Estate',
          'Regions',
          'Zones',
          'Coupon Settings',
          'Coupons',
          'Estimates',
        ],
      },
      {
        key: 'configuration_ai',
        text: 'AI',
        description: 'Artificial intelligence configuration and policies',
        allowed_roles: ['admin'],
        abilities: ['ai-config'],
        module: 'ai',
        icon: '$sparkles_v2',
        value: '/dashboard/ai-config',
        value_v2: `/app/ai-config`,
      },
      {
        key: 'configuration_integrations',
        text: 'Integrations',
        description: 'Third-party integrations and marketplace',
        allowed_roles: ['admin', 'property-manager'],
        icon: '$globe_alt_v2',
        value: '/dashboard/management',
        value_v2: `/app/integrations-v2`,
      },
    ],
  },
  {
    key: 'feeds',
    text: 'Sync',
    description: 'Data sync and integrations',
    icon: '$rss_v2',
    value: '/dashboard/feeds',
    value_v2: `/app/feeds`,
    allowed_roles: ['admin'],
    module: 'ops',
  },
  {
    key: 'analytics',
    text: 'Analytics',
    description: 'Business intelligence and performance analytics',
    isPrivate: true,
    allowed_roles: [
      'admin',
      'field-pm',
      'property-manager',
      'coordinator',
      'hotel-reception',
      'communication-manager',
      'sales-manager',
    ],
    demo_only: true,
    icon: '$chart_bar_v2',
    abilities: [
      'bi-reviews',
      'bi-cleaning',
      'bi-revenue',
      'bi-accounting',
      'bi-comm',
      'bi-pm',
      'bi-invest',
      'bi-inventory',
    ],
    module: 'bi',
    value: '/dashboard/performance',
    value_v2: `/app/dashboard/performance`,
  },
]

// Function to apply module version overrides
const applyModuleVersions = (items, versions) => {
  if (!versions || Object.keys(versions).length === 0) {
    return items
  }

  return items.map(item => {
    const newItem = { ...item }

    // Check if this item has a key that needs version override
    if (item.key && versions[item.key]) {
      const versionConfig = versions[item.key]

      // Override value for version 2 only (value is already v1 by default)
      if (versionConfig.module_version === 2 && item.value_v2) {
        newItem.value = null
        newItem.v2 = `${getV2BaseUrl()}${item.value_v2}`
      } else {
        newItem.value = item.value
        newItem.v2 = null
      }
      // No need to handle v1 - the default value is already v1
    }

    // Recursively apply to submenu items
    if (item.submenu) {
      newItem.submenu = applyModuleVersions(item.submenu, versions)
    }

    return newItem
  })
}

const getMenuItems = (productType, moduleVersions = {}) => {
  let baseMenuItems = []

  switch (productType) {
    case 'boom-host':
      baseMenuItems = menuItemsHost
      break
    default:
      baseMenuItems = menuItemsPro
      break
  }

  // Apply module version overrides
  if (moduleVersions && Object.keys(moduleVersions).length > 0) {
    baseMenuItems = applyModuleVersions(baseMenuItems, moduleVersions)
  }

  return baseMenuItems
}

export { menuItemsPro, menuItemsHost, getMenuItems }
export default menuItemsPro

/*
  menu-items.js

  2025-12-24: Removed BAM Assistant, Analytics V2, and Superadmin menu items
  2025-12-23: Consolidated Analytics V2 into single menu item with tabs (Revenue, Reviews, Cleaning, Guests)
  2025-12-21: Added value_v2 URLs for all menu items to support React app navigation
  2025-07-22: Added Onboarding menu item for boom-host users with product_type filter
  2025-05-29: Added description fields to all menu items for enhanced search functionality
  2025-05-23: Added 'Processes' menu item under Operations section for process management roles, linking to listing-task-templates page
  2024-05-24: Added 'Websites' menu item under Marketing section for admin role, linking to website builder page
*/
